/**
 * Resolves URLs to a user's tenant memberships (the "Go to {tenant}"
 * shortcut on /me + similar). Agency-saas is subdomain-only — every tenant
 * lives at {slug}.zerotohero.network (or a custom whitelisted domain
 * configured per-tenant by the platform admin).
 *
 * Why this composable exists at all: AppShell.vue still has the zth-era
 * community/marketplace nav slots; this gives it a working URL builder
 * without dragging in the zth subdomain-vs-path-based-vs-custom-domain
 * branching that doesn't apply here.
 *
 * Architectural decision (admin-led, subdomain-only):
 *   - All tenants are admin-provisioned. No self-serve signup, no public
 *     marketplace listing.
 *   - The "Browse" and "Create" buttons that used to live in the user-mode
 *     sidebar have been removed at the call site.
 *   - Each tenant gets {slug}.{platformRootDomain}, or a custom domain set
 *     via /api/admin/tenants on the platform admin console.
 *
 * Triggers that would force a re-think (subdomain-only no longer enough):
 *   - Need to onboard tenants with zero DNS / cert provisioning lag
 *   - SEO requirement where tenants must share the platform's domain auth
 *   - Email clients / link-preview tools that strip subdomains
 */

interface TenantishLink {
  tenant_slug?: string
  slug?: string
  url?: string | null
  domain?: string | null
}

export const useCommunityLinks = () => {
  const config = useRuntimeConfig()

  const getTenantUrl = (community: TenantishLink): string => {
    const slug = community.tenant_slug ?? community.slug
    if (!slug) return '#'

    const rootDomain = String(config.public.platformRootDomain || 'zerotohero.network').toLowerCase().trim()

    // 1. Backend-provided absolute URL wins ONLY when it's a custom
    //    Enterprise domain (i.e. not on our platform root). The backend
    //    builds Pro-plan URLs from PLATFORM_DOMAIN which may not match
    //    the browser's actual host in dev (e.g. backend says
    //    "https://kangaroo.agency-saas.local", browser is on
    //    "kangaroo.localhost:3000"). Re-build those from the current host.
    if (community.url) {
      const looksPlatformBuilt = community.url.includes(`.${rootDomain}`)
        || community.url.includes('.agency-saas.local')
        || community.url.includes('.agency-saas.au')
      if (!looksPlatformBuilt) return community.url
    }

    // 2. Build {slug}.{rootDomain} on the current scheme so dev (.localhost)
    //    and prod (zerotohero.network) both work without env mismatches.
    const protocol = import.meta.client ? window.location.protocol : 'https:'

    if (rootDomain === 'localhost' && import.meta.client) {
      // Local dev: preserve the current port so `kangaroo.localhost:3000` works.
      const port = window.location.port ? `:${window.location.port}` : ''
      return `${protocol}//${slug}.localhost${port}/`
    }

    return `${protocol}//${slug}.${rootDomain}/`
  }

  return {
    getCommunityHref: getTenantUrl,
    getCommunityFeedHref: getTenantUrl,
    getCommunityManageHref: getTenantUrl,
    isSubdomainCommunity: () => true
  }
}
